import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { instance } from '../../axios/AxiosInstance';
import AppLayout from '../../components/AppLayout/AppLayout';
import { useTheme } from '../../context/ThemeContext';
import { useUser, type User } from '../../context/UserContext';
import { GLUBES_BRAND_PRIMARY_HEX } from '../../theme/createGlubesMuiTheme';
import { joinStoredPhone, splitStoredPhone } from '../../utils/phoneSplit';
import { getRoleLabel } from '../../utils/roleLabel';

const profileThemeColorPickerLabelSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1.5,
  cursor: 'pointer',
  width: 'fit-content',
  '& input[type="color"]': {
    width: 52,
    height: 40,
    padding: 0,
    border: 'none',
    borderRadius: 1,
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },
  '& input[type="color"]::-webkit-color-swatch-wrapper': {
    padding: 0,
  },
  '& input[type="color"]::-webkit-color-swatch': {
    borderRadius: 8,
    border: (t) => `1px solid ${t.palette.divider}`,
  },
  '& input[type="color"]::-moz-color-swatch': {
    borderRadius: 8,
    border: (t) => `1px solid ${t.palette.divider}`,
  },
};

type ProfileThemeColorRowProps = {
  modeHeading: string;
  pickerValue: string;
  onPick: (hex: string) => void;
  onResetToDefault: () => void;
  resetDisabled: boolean;
  pickerAriaLabel: string;
};

function ProfileThemeColorRow({
  modeHeading,
  pickerValue,
  onPick,
  onResetToDefault,
  resetDisabled,
  pickerAriaLabel,
}: ProfileThemeColorRowProps) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={600}>
        {modeHeading}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Box component="label" sx={profileThemeColorPickerLabelSx}>
          <Typography component="span" variant="body2" color="text.secondary">
            Өнгө сонгох
          </Typography>
          <input type="color" value={pickerValue} onChange={(e) => onPick(e.target.value)} aria-label={pickerAriaLabel} />
        </Box>
        <Button
          type="button"
          variant="outlined"
          onClick={onResetToDefault}
          disabled={resetDisabled}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Анхны өнгө рүү буцаах
        </Button>
      </Stack>
    </Stack>
  );
}

export default function ProfilePage() {
  const { user, setUser } = useUser();
  const {
    primaryMainLight,
    primaryMainDark,
    setPrimaryMainLight,
    setPrimaryMainDark,
    resolvedTheme,
  } = useTheme();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobileExt, setMobileExt] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');

  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdSuccess, setPwdSuccess] = useState<string | null>(null);
  const [savingPwd, setSavingPwd] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const lightColorPickerValue = useMemo(
    () => primaryMainLight ?? GLUBES_BRAND_PRIMARY_HEX.light,
    [primaryMainLight]
  );
  const darkColorPickerValue = useMemo(
    () => primaryMainDark ?? GLUBES_BRAND_PRIMARY_HEX.dark,
    [primaryMainDark]
  );

  const applyUserToForm = useCallback((u: User) => {
    setFirstName(u.first_name ?? '');
    setLastName(u.last_name ?? '');
    const { ext, number } = splitStoredPhone(u.phone);
    setMobileExt(ext);
    setMobileNumber(number);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingProfile(true);
    setProfileError(null);
    instance
      .get<{ user: User }>('users')
      .then((res) => {
        if (cancelled) return;
        setUser(res.data.user);
        applyUserToForm(res.data.user);
      })
      .catch(() => {
        if (!cancelled) setProfileError('Мэдээлэл ачааллахад алдаа гарлаа.');
      })
      .finally(() => {
        if (!cancelled) setLoadingProfile(false);
      });
    return () => {
      cancelled = true;
    };
  }, [setUser, applyUserToForm]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(null);
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      setProfileError('Овог, нэрээ бүрэн оруулна уу.');
      return;
    }
    setSavingProfile(true);
    try {
      const phone = joinStoredPhone(mobileExt, mobileNumber);
      await instance.put('users', {
        first_name: fn,
        last_name: ln,
        phone,
      });
      const { data } = await instance.get<{ user: User }>('users');
      setUser(data.user);
      applyUserToForm(data.user);
      setProfileSuccess('Хадгалагдлаа.');
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setProfileError(msg || 'Хадгалахад алдаа гарлаа.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError(null);
    setPwdSuccess(null);
    if (!oldPassword || !newPassword) {
      setPwdError('Одоогийн болон шинэ нууц үгээ оруулна уу.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError('Шинэ нууц үг таарахгүй байна.');
      return;
    }
    if (newPassword.length < 8) {
      setPwdError('Шинэ нууц үг хамгийн багадаа 8 тэмдэгт байна.');
      return;
    }
    setSavingPwd(true);
    try {
      await instance.post('change-password', {
        old_password: oldPassword,
        password: newPassword,
      });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwdSuccess('Нууц үг амжилттай шинэчлэгдлээ.');
    } catch (err: unknown) {
      const res =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number; data?: { message?: string } } }).response
          : undefined;
      if (res?.status === 401) {
        setPwdError('Одоогийн нууц үг буруу байна.');
      } else {
        setPwdError(res?.data?.message || 'Нууц үг солиход алдаа гарлаа.');
      }
    } finally {
      setSavingPwd(false);
    }
  };

  const pwdAdornment = (visible: boolean, toggle: () => void) => (
    <InputAdornment position="end">
      <IconButton edge="end" onClick={toggle} aria-label={visible ? 'Нууц үг нуух' : 'Нууц үг харуулах'}>
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <AppLayout>
      <Box component="header" sx={{ mb: 3 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 1, fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
          Профайл
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Хувийн мэдээлэл болон нууц үгээ эндээс шинэчилнэ үү.
        </Typography>
      </Box>

      <Stack spacing={3} sx={{ maxWidth: 560 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Үндсэн өнгө
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {resolvedTheme === 'light'
              ? 'Товчлуур, идэвхтэй холбоос зэрэгт ашиглагдана. Одоогийн цайвар горимын үндсэн өнгийг тохируулна. Нөгөө горимын өнгийг солихын тулд эхлээд харанхуй горим руу шилжинэ үү. Тохиргоо энэ төхөөрөмжийн хөтөч дээр хадгалагдана.'
              : 'Товчлуур, идэвхтэй холбоос зэрэгт ашиглагдана. Одоогийн харанхуй горимын үндсэн өнгийг тохируулна. Нөгөө горимын өнгийг солихын тулд эхлээд цайвар горим руу шилжинэ үү. Тохиргоо энэ төхөөрөмжийн хөтөч дээр хадгалагдана.'}
          </Typography>
          {resolvedTheme === 'light' ? (
            <ProfileThemeColorRow
              modeHeading="Цайвар горим"
              pickerValue={lightColorPickerValue}
              onPick={setPrimaryMainLight}
              onResetToDefault={() => setPrimaryMainLight(null)}
              resetDisabled={primaryMainLight == null}
              pickerAriaLabel="Цайвар горимын үндсэн өнгө сонгох"
            />
          ) : (
            <ProfileThemeColorRow
              modeHeading="Харанхуй горим"
              pickerValue={darkColorPickerValue}
              onPick={setPrimaryMainDark}
              onResetToDefault={() => setPrimaryMainDark(null)}
              resetDisabled={primaryMainDark == null}
              pickerAriaLabel="Харанхуй горимын үндсэн өнгө сонгох"
            />
          )}
        </Paper>

        <Paper
          component="form"
          variant="outlined"
          onSubmit={handleSaveProfile}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Хувийн мэдээлэл
          </Typography>
          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError(null)}>
              {profileError}
            </Alert>
          )}
          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProfileSuccess(null)}>
              {profileSuccess}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Имэйл"
              value={user?.email ?? ''}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
              helperText="Имэйлийг зөвхөн админ өөрчилнө"
            />
            <TextField
              label="Эрх"
              value={user?.role ? getRoleLabel(user.role) : ''}
              fullWidth
              disabled
              slotProps={{ input: { readOnly: true } }}
              helperText="Эрхийг зөвхөн админ өөрчилнө"
            />
            <TextField
              label="Нэр"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              fullWidth
              required
              disabled={loadingProfile || savingProfile}
              autoComplete="given-name"
            />
            <TextField
              label="Овог"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              fullWidth
              required
              disabled={loadingProfile || savingProfile}
              autoComplete="family-name"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Утас (код)"
                value={mobileExt}
                onChange={(e) => setMobileExt(e.target.value)}
                placeholder="+976"
                sx={{ width: { xs: 1, sm: 140 } }}
                disabled={loadingProfile || savingProfile}
                autoComplete="tel-country-code"
              />
              <TextField
                label="Утасны дугаар"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                fullWidth
                disabled={loadingProfile || savingProfile}
                autoComplete="tel-national"
              />
            </Stack>
            <Box>
              <Button type="submit" variant="contained" disabled={loadingProfile || savingProfile}>
                {savingProfile ? 'Хадгалж байна…' : 'Хадгалах'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper
          component="form"
          variant="outlined"
          onSubmit={handleChangePassword}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Нууц үг солих
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {pwdError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPwdError(null)}>
              {pwdError}
            </Alert>
          )}
          {pwdSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPwdSuccess(null)}>
              {pwdSuccess}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="Одоогийн нууц үг"
              type={showOldPwd ? 'text' : 'password'}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              fullWidth
              autoComplete="current-password"
              disabled={savingPwd}
              slotProps={{
                input: {
                  endAdornment: pwdAdornment(showOldPwd, () => setShowOldPwd((v) => !v)),
                },
              }}
            />
            <TextField
              label="Шинэ нууц үг"
              type={showNewPwd ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              disabled={savingPwd}
              helperText="Хамгийн багадаа 8 тэмдэгт"
              slotProps={{
                input: {
                  endAdornment: pwdAdornment(showNewPwd, () => setShowNewPwd((v) => !v)),
                },
              }}
            />
            <TextField
              label="Шинэ нууц үг (давтах)"
              type={showConfirmPwd ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              disabled={savingPwd}
              slotProps={{
                input: {
                  endAdornment: pwdAdornment(showConfirmPwd, () => setShowConfirmPwd((v) => !v)),
                },
              }}
            />
            <Box>
              <Button type="submit" variant="outlined" disabled={savingPwd}>
                {savingPwd ? 'Шинэчилж байна…' : 'Нууц үг солих'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </AppLayout>
  );
}
